import { computed, onBeforeUnmount, ref, shallowRef, watch, type Ref } from "vue";
import { useApolloClient } from "@vue/apollo-composable";
import {
  GlobalSearchType,
  GlobalSearchSort,
  GSSearchV2Document,
  type GSSearchV2Query,
  type GSSearchV2QueryVariables,
} from "@/graphql";
import { type GlobalSearchUIType, requiredRightBySearchType } from "./GlobalSearchTypes";
import type { GlobalSearchResult } from "./GlobalSearchNavigation";
import { hasRightOnAnySite } from "@/functions/authorizationState";

type GlobalSearchOptions = {
  query: Ref<string>;
  selectedTypes: Ref<GlobalSearchUIType[]>;
  pageSize: number;
  includeArchived?: Ref<boolean>;
  siteIds?: Ref<number[]>;
  boardId?: Ref<number | undefined>;
  sort?: Ref<GlobalSearchSort>;
};

function uniqueSearchResults(results: GlobalSearchResult[]) {
  const seen = new Set<string>();
  return results.filter((result) => {
    const key = `${result.type}:${result.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function useGlobalSearch(options: GlobalSearchOptions) {
  const { client } = useApolloClient();
  const backendResults = shallowRef<GlobalSearchResult[]>([]);
  const backendCounts = ref(new Map<GlobalSearchType, number>());
  const totalBackendCount = ref(0);
  const hasNextPage = ref(false);
  const loading = ref(false);
  const error = ref<Error>();
  const offset = ref(0);
  const includeArchived = options.includeArchived ?? ref<boolean>(true);
  const siteIds = options.siteIds ?? ref<number[]>([]);
  const boardId = options.boardId ?? ref<number>();
  const sort = options.sort ?? ref(GlobalSearchSort.RELEVANCE);
  let debounceTimer: number | undefined;
  let requestVersion = 0;

  const availableTypes = computed<GlobalSearchUIType[]>(() => {
    return Object.values(GlobalSearchType).filter((type) => {
      const right = requiredRightBySearchType[type];
      return right == null || hasRightOnAnySite(right);
    });
  });

  const results = computed(() => {
    return [...backendResults.value].sort((left, right) => {
      if (sort.value === GlobalSearchSort.NAME_ASC) return left.title.localeCompare(right.title);
      if (sort.value === GlobalSearchSort.NAME_DESC) return right.title.localeCompare(left.title);
      const leftCreatedAt = left.createdAt == null ? 0 : new Date(left.createdAt).getTime();
      const rightCreatedAt = right.createdAt == null ? 0 : new Date(right.createdAt).getTime();
      if (
        sort.value === GlobalSearchSort.CREATED_AT_ASC ||
        sort.value === GlobalSearchSort.CREATED_AT_DESC
      ) {
        if (left.createdAt == null) return 1;
        if (right.createdAt == null) return -1;
        return sort.value === GlobalSearchSort.CREATED_AT_ASC
          ? leftCreatedAt - rightCreatedAt
          : rightCreatedAt - leftCreatedAt;
      }
      return right.score - left.score;
    });
  });

  const counts = computed(() => {
    return new Map<GlobalSearchUIType, number>(backendCounts.value);
  });

  async function executeSearch(append = false) {
    const query = options.query.value.trim();
    const backendTypes = options.selectedTypes.value;
    const version = ++requestVersion;
    error.value = undefined;

    if (query.length < 2 || backendTypes.length === 0) {
      backendResults.value = [];
      backendCounts.value = new Map();
      totalBackendCount.value = 0;
      hasNextPage.value = false;
      loading.value = false;
      return;
    }

    loading.value = true;
    try {
      const nextOffset = append ? offset.value : 0;
      const response = await client.query<GSSearchV2Query, GSSearchV2QueryVariables>({
        query: GSSearchV2Document,
        variables: {
          input: {
            searchString: query,
            types: backendTypes,
            first: options.pageSize,
            offset: nextOffset,
            includeArchived: includeArchived.value,
            siteIds: siteIds.value,
            boardId: boardId.value,
            sort: sort.value,
          },
        },
        fetchPolicy: "network-only",
      });
      if (version !== requestVersion) {
        return;
      }
      const payload = response.data.GSSearchV2;
      backendResults.value = uniqueSearchResults(
        append ? [...backendResults.value, ...payload.results] : payload.results,
      );
      backendCounts.value = new Map(payload.counts.map((count) => [count.type, count.count]));
      totalBackendCount.value = payload.totalCount;
      hasNextPage.value = payload.hasNextPage;
      offset.value = nextOffset + payload.results.length;
    } catch (caughtError: unknown) {
      if (version === requestVersion) {
        error.value = caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      }
    } finally {
      if (version === requestVersion) {
        loading.value = false;
      }
    }
  }

  function scheduleSearch() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => void executeSearch(), 225);
  }

  watch(
    [options.query, options.selectedTypes, includeArchived, siteIds, boardId, sort],
    scheduleSearch,
    { deep: true, immediate: true },
  );
  onBeforeUnmount(() => {
    requestVersion += 1;
    window.clearTimeout(debounceTimer);
  });

  return {
    availableTypes,
    results,
    counts,
    totalCount: totalBackendCount,
    hasNextPage,
    loading,
    error,
    refresh: () => executeSearch(),
    loadMore: () => executeSearch(true),
  };
}
